import type { Node } from '../../../core/models/scene';
import type { Scenario } from '../../../core/assistant/stores/scenariosStore';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import type { AtlasPlanningSeed } from '../models/AtlasPlanningSeed';
import { buildPeopleHierarchy } from './buildPeopleChartScene';
import { scenarioDeltaToPositionIntents } from './scenarioDeltaToPositionIntents';
import { toIntent } from './commitScenarioDelta';
import { orgProjectionCompletenessError } from './orgProjectionCompleteness';

/** Translate once; unsupported edits abort the whole copy instead of disappearing. */
export function buildPlanningSeed(
  model: OrgModelProjection, nodes: readonly Node[], scenario: Scenario, sourceChangeSetId?: string,
): AtlasPlanningSeed {
  const incomplete = orgProjectionCompletenessError(model);
  if (incomplete) throw incomplete;
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const positions = new Map(model.positions.map((position) => [position.id, position]));
  const people = new Set(model.people.map((person) => person.id));
  const hierarchy = buildPeopleHierarchy(model);
  const translation = scenarioDeltaToPositionIntents({
    delta: scenario.delta,
    recordIdByNodeId: (id) => {
      if (people.has(id) || positions.has(id)) return id;
      const recordId = nodeById.get(id)?.attrs?.folderRecordId;
      return typeof recordId === 'string' ? recordId : undefined;
    },
    positionByRecordId: (id) => {
      const position = positions.get(id) ?? hierarchy.positionByPersonId.get(id);
      return position ? {
        id: position.id, title: position.label,
        department: typeof position.props.department === 'string' ? position.props.department : undefined,
      } : undefined;
    },
    isPersonRecord: (id) => people.has(id),
  });
  if (translation.unsupported.length) {
    throw new Error('This draft contains changes that cannot be carried into a protected plan. Keep the original draft and reconcile those changes first.');
  }
  const effectiveAt = model.asOf;
  return {
    baseGraphVersion: model.graphVersion, effectiveAt, sourceChangeSetId,
    intents: translation.intents.map((core) => toIntent(core, {
      intentId: crypto.randomUUID(), expectedGraphVersion: model.graphVersion,
      ownerDiagramId: 'pending-planning-owner', effectiveAt,
    })),
  };
}
